import { useMutation, useQuery } from '@tanstack/react-query'
import {
    sendTeamInvitation,
    validateTeamInvitation,
    acceptTeamInvitation,
    cancelTeamInvitation,
    getTeamInvitations,
} from './invitationsApi'

/**
 * Hook to manage team invitations
 * Groups all invitation-related queries and mutations
 *
 * @param {Object} options - Configuration options
 * @param {string} options.accountID - Account ID for fetching invitations
 * @param {string} options.invitationToken - Token for validating invitation
 * @param {string} options.invitationID - ID for validating invitation (alternative to token)
 *
 * @returns {Object} Invitation queries and mutations
 */
export const useInvitations = ({ accountID, invitationToken, invitationID } = {}) => {
    // Query: Get pending invitations for an account
    const {
        data: invitationsData,
        isLoading: loadingInvitations,
        refetch: refetchInvitations,
    } = useQuery({
        queryKey: ['teamInvitations', accountID],
        queryFn: () => getTeamInvitations(accountID),
        enabled: !!accountID,
        staleTime: 30000, // Cache for 30 seconds
    })

    // Query: Validate team invitation (public - no auth required)
    const {
        data: validationData,
        isLoading: validatingInvitation,
        error: validationError,
    } = useQuery({
        queryKey: ['validateTeamInvitation', invitationToken, invitationID],
        queryFn: () => validateTeamInvitation(invitationToken, invitationID),
        enabled: !!(invitationToken || invitationID),
        retry: false,
        staleTime: 0, // Always fetch fresh
    })

    // Mutation: Send team invitation
    const { mutate: sendInvitation, isPending: isSendingInvitation } = useMutation({
        mutationFn: ({ accountID, email, role }) => sendTeamInvitation(accountID, email, role),
    })

    // Mutation: Accept team invitation
    const { mutate: acceptInvitation, isPending: isAcceptingInvitation } = useMutation({
        mutationFn: ({ invitationToken }) => acceptTeamInvitation(invitationToken),
    })

    // Mutation: Cancel team invitation
    const { mutate: cancelInvitation, isPending: isCancellingInvitation } = useMutation({
        mutationFn: ({ invitationID }) => cancelTeamInvitation(invitationID),
    })

    return {
        // Invitations list
        invitations: invitationsData?.invitations || [],
        totalInvitations: invitationsData?.totalInvitations || 0,
        loadingInvitations,
        refetchInvitations,

        // Invitation validation
        invitation: validationData?.invitation,
        validatingInvitation,
        validationError,

        // Mutations
        sendInvitation,
        isSendingInvitation,
        acceptInvitation,
        isAcceptingInvitation,
        cancelInvitation,
        isCancellingInvitation,
    }
}
